import { useCallback } from 'react';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { InboxThreadItemAlert_ActionType } from '@weave/schema-gen-ts/dist/shared/notification/alerts.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when an INBOX_THREAD_ITEM event is received.
 */
export const useInboxThreadItemWSHandler = () => {
  const { upsertThreadItem, removeVoiceThreadItem } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { isCallRecordItem } = SMSDataSharedUtils.threadItemsUtils;

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.INBOX_THREAD_ITEM>>(
    async (event) => {
      const { params } = event;

      if (isCallRecordItem(params.item)) {
        switch (params.actionType) {
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_INBOUND:
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_OUTBOUND:
            await upsertThreadItem({
              matchValues: {
                threadId: params.threadId,
                threadItemId: params.item.callRecord.threadCallId,
                locationId: params.item.callRecord.locationId,
                entityType:
                  params.item.callRecord.callResult === CallResult.CALL_RESULT_VOICEMAIL
                    ? EntityType.ENTITY_TYPE_VOICEMAIL
                    : EntityType.ENTITY_TYPE_CALL,
              },
              updater: () => params.item,
              isOptimistic: false,
            });
            break;
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_DELETE: // filters out the threadItem: only voicemail is supported now.
            if (isCallRecordItem(params.item)) {
              removeVoiceThreadItem({
                threadId: params.threadId,
                threadCallId: params.item.callRecord.threadCallId,
                locationId: params.item.callRecord.locationId,
              });
            }
            break;
        }
      }
    },
    [upsertThreadItem, isCallRecordItem]
  );

  return wsEventHandler;
};

export const useInboxThreadItemWSSubscription = () => {
  const handler = useInboxThreadItemWSHandler();
  return useWebsocketEventSubscription(NotificationType.INBOX_THREAD_ITEM, handler);
};
